import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
    selectAllUserAppointmentsAndSlotsByUserAndDate,
    selectAllUserEventsByUserAndDate
} from '../../../store/calendarSelectors';
import {
    setOpenAppointmentSchedulerPanel,
    setUsersCurrentAppointment
} from '../../../store/calendarService';
import { selectAllCourseModules } from '../../../store/courseSelectors';
import {
    loadAppointmentTypeDetails,
    loadContractDetailsForContractId,
    loadServiceDetailsForContractId
} from '../../../store/directusService';
import { setCurrentlySelectedParticipant } from '../../../store/participantService';
import { hasRole } from '../../../utils/userRoles';
import UserAppointmentCreate from '../calendarForUsers/UserAppointmentCreate';
import UserAppointmentSlotsEdit from '../calendarForUsers/UserAppointmentSlotsEdit';
import {
    configCourseEventContent,
    configParticipantEventContent,
    configUserEventContent,
    eventEditRoles,
    formatFullCalendarTimes,
    getBusinessHours,
    mapColors,
    mapCommonParams,
    mapUserEventColors
} from '../utils/calendarUtils';

import SchedulingAssistantCalendar from './SchedulingAssistantCalendar';

import app from '../../../app.module.css';

const _DOUBLE_CLICK = 300;
let userClickCount = 0;

const SchedulingAssistantCalendarManagement = ({
    selectedAdvisers,
    selectedAdviser,
    isAppointmentSlots,
    onGetCurrentDate,
    currentDateView
}) => {
    // HOOKS
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // LOCAL STATE
    const [courseEvents, setCourseEvents] = useState([]);
    const [userEvents, setUserEvents] = useState([]);
    const [events, setEvents] = useState([]);
    const [newEvent, setNewEvent] = useState({});
    const [currentEvent, setCurrentEvent] = useState({});
    const [currentAdviser, setCurrentAdviser] = useState({});
    const [currentResourceId, setCurrentResourceId] = useState('');
    const [advisers, setAdvisers] = useState([]);
    const [resources, setResources] = useState([]);
    const [currentParticipantEntry, setCurrentParticipantEntry] = useState({});
    const [isPTRequestSent, setIsPTRequestSent] = useState(false);

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const { roles } = loggedInUser;
    const contractDetailsById = useSelector(
        (state) => state.entities.directusService.contractDetailsById
    );
    const servicesByContractId = useSelector(
        (state) => state.entities.directusService.servicesByContractId
    );
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const appointmentTypes = useSelector(
        (state) => state.entities.directusService.appointmentTypeDetails
    );
    const participants = useSelector((state) => state.entities.participantService.participants);
    const usersCurrentAppointment = useSelector(
        (state) => state.entities.calendarService.usersCurrentAppointment
    );
    const allUserAppointmentsAndSlotsByUserAndDate = useSelector(
        selectAllUserAppointmentsAndSlotsByUserAndDate
    );
    const appointmentsAndSlotsByUserAndDate = useSelector(
        (state) => state.entities.calendarService.appointmentsAndSlotsByUserAndDate
    );
    const appointmentsAndSlotsByUserAndDateMetaData = useSelector(
        (state) => state.entities.calendarService.appointmentsAndSlotsByUserAndDateMetaData
    );
    const courseModules = useSelector(selectAllCourseModules);
    const courseModulesByUsersAndDateMetaData = useSelector(
        (state) => state.entities.courseService.courseModulesByUsersAndDateMetaData
    );
    const allUserEventsByUserAndDate = useSelector(selectAllUserEventsByUserAndDate);
    const userEventsByUserAndDateMetaData = useSelector(
        (state) => state.entities.calendarService.userEventsByUserAndDateMetaData
    );
    const openAppointmentSchedulerPanel = useSelector(
        (state) => state.entities.calendarService.openAppointmentSchedulerPanel
    );

    // CONFIG FNS
    const convertTimeToNumber = (time) => {
        if (!time) return;
        const [hr, min] = time.split(':');
        return +hr + +min / 60;
    };

    const sortEvents = (events) => {
        const resourceIds = [...new Set(events.map((el) => el.resourceId))];
        const resourceSorted = resourceIds.map((el) =>
            events.filter((entry) => entry.resourceId === el)
        );
        return resourceSorted
            .map((el) => {
                const uniqueDates = [...new Set(el.map((entry) => entry.date))];
                const dateSorted = uniqueDates.map((entry) =>
                    el.filter((item) => item.date === entry)
                );
                const timeToNumbers = dateSorted.map((dArr) => {
                    return dArr
                        .map((entry) => ({
                            ...entry,
                            startNumber: convertTimeToNumber(entry.displayStart),
                            endNumber: convertTimeToNumber(entry.displayEnd)
                        }))
                        .sort((a, b) => (a.startNumber > b.startNumber ? 1 : -1));
                });
                return timeToNumbers
                    .map((ele) =>
                        ele.map((entry, i, arr) => {
                            let hasOverlap = false;
                            if (arr[i].endNumber > arr[i + 1]?.startNumber) hasOverlap = true;
                            if (arr[i - 1]?.endNumber > arr[i].startNumber) hasOverlap = true;
                            return { ...entry, hasOverlap };
                        })
                    )
                    .flat();
            })
            .flat();
    };

    const configEvents = () => {
        const businessHours = getBusinessHours();
        let newEvents;
        if (
            !allUserAppointmentsAndSlotsByUserAndDate ||
            allUserAppointmentsAndSlotsByUserAndDate.length < 1
        )
            newEvents = [{}];
        else
            newEvents = allUserAppointmentsAndSlotsByUserAndDate?.map((el) => {
                const { date, start, end } = mapCommonParams(el);
                const { participantEntry, participant, slots, filledSlots } =
                    configParticipantEventContent(el, participants);
                const { color, textColor } = mapColors(el.id, usersCurrentAppointment?.id);
                const editable = false;
                const resourceEditable = el.userId === currentResourceId;
                const orderId = resources.find((entry) => entry.id === el.userId)?.orderId;
                const classes = `timeview-event ${
                    !isAppointmentSlots || !resourceEditable ? 'non-click-event' : ''
                }
                    ${isAppointmentSlots && !slots ? 'hide-event' : ''}`;
                return {
                    id: el.id,
                    eventType: 'participant',
                    viewType: 'timeView',
                    className: classes,
                    hasLink: true,
                    slots,
                    filledSlots,
                    location: el.location,
                    additionalDetails: el.additionalDetails,
                    participantEntry,
                    participant,
                    appointmentType: el.type,
                    displayStart: el.startTime,
                    displayEnd: el.endTime,
                    orderId,
                    color,
                    textColor,
                    date,
                    start,
                    end,
                    constraint: businessHours,
                    resourceId: el.userId,
                    resourceEditable,
                    editable,
                    draggable: false
                };
            });
        const updatedEvents = events?.filter((el) => el.resourceId !== currentResourceId);
        const sortedEvents = sortEvents(newEvents);
        setEvents([...updatedEvents, ...sortedEvents]);

        if (newEvents.length > 0) {
            const cur = events?.find((el) => el.id === usersCurrentAppointment?.id);
            if (cur && cur.resourceId === currentResourceId) setCurrentEvent(cur);
        }
    };

    const configModuleEvents = () => {
        const modules = courseModules?.filter((el) => el.date)?.filter((el) => el.userId);
        let events;
        if (modules?.length < 1) events = [];
        else
            events = modules?.map((el) => {
                const { date, start, end } = mapCommonParams(el);
                const content = configCourseEventContent(el);
                const orderId = resources?.find((entry) => entry.id === el.userId)?.orderId;
                const classes = `timeview-event non-click-event ${
                    isAppointmentSlots ? 'hide-event' : ''
                }`;
                return {
                    id: el.id,
                    eventType: 'courseModule',
                    className: classes,
                    viewType: 'timeView',
                    content,
                    date,
                    start,
                    end,
                    displayStart: el.startTime,
                    displayEnd: el.endTime,
                    orderId,
                    editable: false,
                    draggable: false,
                    resourceId: el.userId
                };
            });
        setCourseEvents([...courseEvents, ...events]);
    };

    const configUserEvents = () => {
        let events;
        if (!allUserEventsByUserAndDate || allUserEventsByUserAndDate.length < 1) events = [];
        else
            events = allUserEventsByUserAndDate.map((el) => {
                const { date, start, end } = mapCommonParams(el);
                const { color, textColor } = mapUserEventColors(el.id);
                const { displayOccurrence, content } = configUserEventContent(el);
                const orderId = resources?.find((entry) => entry.id === el.userId)?.orderId;
                const classes = `timeview-event non-click-event ${
                    isAppointmentSlots ? 'hide-event' : ''
                }`;
                return {
                    id: el.id,
                    eventType: 'userEvent',
                    className: classes,
                    viewType: 'timeView',
                    content,
                    color,
                    textColor,
                    date,
                    start,
                    end,
                    orderId,
                    displayStart: el.startTime,
                    displayEnd: el.endTime,
                    editable: false,
                    draggable: false,
                    repeat: el.repeat,
                    repeatUntilDate: el.repeatUntilDate,
                    displayOccurrence,
                    resourceId: el.userId
                };
            });
        setUserEvents(events);
    };

    const configResources = () => {
        const businessHours = getBusinessHours();
        let resources;
        if (!advisers.length) resources = [];
        else
            resources = advisers
                .map((el, i) => {
                    return el
                        ? {
                              id: el.id,
                              title: `${el.firstName} ${el.lastName}`,
                              orderId: i,
                              active: el.id === selectedAdviser.id,
                              businessHours: businessHours
                          }
                        : null;
                })
                .filter((el) => el);
        setResources(resources);
    };

    const sidePanel = document.querySelector('.scheduling-sidebar .adviser-panel .added-advisers');
    const table = document.querySelector('.fc .fc-timeline .fc-scroller-liquid-absolute');
    sidePanel?.addEventListener('scroll', () => {
        table.scrollTop = sidePanel.scrollTop;
        table.style.top = `${sidePanel.scrollTop}px`;
    });

    // USEEFFECTS
    useEffect(() => {
        appointmentTypes?.length < 1 && dispatch(loadAppointmentTypeDetails());
    }, []);

    useEffect(() => {
        setAdvisers(selectedAdvisers);
        setCurrentAdviser(selectedAdvisers[selectedAdvisers.length - 1]);
        setCurrentResourceId(selectedAdvisers[selectedAdvisers.length - 1]?.id);
        configResources();
    }, [selectedAdvisers]);

    useEffect(() => {
        if (Object.keys(selectedAdviser).length < 1) return;
        setCurrentResourceId(selectedAdviser.id);
        setCurrentAdviser(
            !('contractIds' in selectedAdviser)
                ? { ...selectedAdviser, contractIds: selectedAdviser.contracts || [] }
                : selectedAdviser
        );
        configResources();
    }, [selectedAdviser]);

    useEffect(() => {
        if (
            appointmentsAndSlotsByUserAndDateMetaData?.last &&
            allUserAppointmentsAndSlotsByUserAndDate.length &&
            currentResourceId &&
            participants?.length
        ) {
            configEvents();
        }
    }, [
        currentResourceId,
        allUserAppointmentsAndSlotsByUserAndDate,
        appointmentsAndSlotsByUserAndDateMetaData,
        participants
    ]);

    useEffect(() => {
        const calendarTable = document.querySelector('.fc-timeline-body');
        if (!calendarTable) return;
        calendarTable && !advisers.length
            ? (calendarTable.style.visibility = 'hidden')
            : (calendarTable.style.visibility = 'visible');
        configResources();
    }, [advisers]);

    useEffect(() => {
        configEvents();
    }, [usersCurrentAppointment]);

    useEffect(() => {
        if (courseModulesByUsersAndDateMetaData?.last && courseModules?.length)
            configModuleEvents();
    }, [courseModulesByUsersAndDateMetaData, courseModules]);

    useEffect(() => {
        if (userEventsByUserAndDateMetaData?.last && allUserEventsByUserAndDate?.length)
            configUserEvents();
    }, [userEventsByUserAndDateMetaData, allUserEventsByUserAndDate]);

    useEffect(() => {
        configEvents();
        configModuleEvents();
        configUserEvents();
    }, [isAppointmentSlots]);

    useEffect(() => {
        if (
            isPTRequestSent &&
            currentParticipant?.ptCode === currentParticipantEntry?.ptCode &&
            contractDetailsById[currentParticipant.contractId] &&
            servicesByContractId[currentParticipant.contractId]
        )
            navigate('/edit_participant');
    }, [isPTRequestSent, contractDetailsById, servicesByContractId, currentParticipant]);

    // EVENT HANDLERS
    const onTogglePanel = (open) => {
        dispatch(setOpenAppointmentSchedulerPanel(open));
    };

    const onSelectEvent = (id) => {
        if (!currentResourceId || !isAppointmentSlots) return;
        setNewEvent({});
        if (id !== usersCurrentAppointment?.id) {
            const selectedAppointment = appointmentsAndSlotsByUserAndDate[
                `${currentResourceId}_${currentDateView}`
            ]?.find((el) => el.id === id);
            selectedAppointment && dispatch(setUsersCurrentAppointment(selectedAppointment));
        }
        if (id === currentEvent.id && usersCurrentAppointment?.userId === currentResourceId) {
            userClickCount++;
            const timeoutId = setTimeout(() => (userClickCount = 0), _DOUBLE_CLICK);
            if (userClickCount > 1) dispatch(setOpenAppointmentSchedulerPanel(true));
            return () => clearTimeout(timeoutId);
        }
    };

    const onSelectSlot = (start, end) => {
        const { date, startTime, endTime } = formatFullCalendarTimes({ start, end });
        setNewEvent({ date, startTime, endTime });
        onTogglePanel(true);
    };

    const onNav = (selectedEvent) => {
        dispatch(setCurrentlySelectedParticipant(selectedEvent.participantEntry));
        const { contractId } = selectedEvent.participantEntry;
        if (!(contractId in contractDetailsById))
            dispatch(loadContractDetailsForContractId(contractId));
        if (!(contractId in servicesByContractId))
            dispatch(loadServiceDetailsForContractId(contractId));
        setCurrentParticipantEntry(selectedEvent.participantEntry);
        setIsPTRequestSent(true);
    };

    // RENDER
    return (
        <div className={app.container}>
            <div className="calendar-wrapper">
                <SchedulingAssistantCalendar
                    resources={resources}
                    events={[...events, ...courseEvents, ...userEvents]}
                    currentEvent={currentEvent}
                    onSelectSlot={onSelectSlot}
                    allowSlotSelect={!isAppointmentSlots}
                    onSelectEvent={onSelectEvent}
                    onChangeResource={configEvents}
                    onNav={onNav}
                    eventEditPermission={
                        hasRole(eventEditRoles, roles) || loggedInUser?.id === currentAdviser?.id
                    }
                    onGetCurrentDate={onGetCurrentDate}
                />

                {openAppointmentSchedulerPanel &&
                    currentAdviser &&
                    (Object.keys(newEvent).length ? (
                        <UserAppointmentCreate
                            newAppointment={newEvent}
                            currentCalendarOwner={currentAdviser}
                            calendar="schedulingAssistant"
                        />
                    ) : (
                        currentEvent.slots && (
                            <UserAppointmentSlotsEdit
                                usersCurrentAppointment={usersCurrentAppointment}
                                calendar="schedulingAssistant"
                            />
                        )
                    ))}
            </div>
        </div>
    );
};

SchedulingAssistantCalendarManagement.propTypes = {
    selectedAdvisers: PropTypes.arrayOf(PropTypes.object),
    selectedAdviser: PropTypes.object,
    isAppointmentSlots: PropTypes.bool,
    onGetCurrentDate: PropTypes.func,
    currentDateView: PropTypes.string
};

export default SchedulingAssistantCalendarManagement;
